import { lazy } from "react";
import type { ComponentType } from "react";

// After a deploy, page chunks get new hashed file names. A tab still running
// the previous version then asks for files that no longer exist (404) and the
// screen goes blank. Reloading once fetches the new index.html and chunks.
// The guard stops a reload loop if the failure is something else.
export const RELOAD_GUARD_KEY = "simulacrum:chunk-reload-at";
const GUARD_MS = 30_000;

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < GUARD_MS;
  } catch {
    return false;
  }
}

export function reloadForNewVersion(): boolean {
  if (reloadedRecently()) return false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: reload anyway; worst case the boundary shows the fallback.
  }
  window.location.reload();
  return true;
}

export function importWithReload<T>(loader: () => Promise<T>): Promise<T> {
  return loader().catch((err: unknown) => {
    if (reloadForNewVersion()) {
      // Keep the page in its loading state while the browser reloads.
      return new Promise<T>(() => {});
    }
    throw err;
  });
}

// Same signature as React.lazy, so every page component type is accepted.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  return lazy(() => importWithReload(loader));
}
